import { storeToRefs } from 'pinia'
import { computed, ref, type ComputedRef, type Ref } from 'vue'

import { chatModel } from '@/entities/messages'
import { useStoreRooms } from '@/entities/room'


import { useCurrentUser } from '@/shared/composables/current-user'
import { useLocalization } from '@/shared/composables/localization'
import type { RoomListItem } from '@/shared/types/app'

export enum RoomItemFilter {
  ALL = 'ALL',
  UNREAD = 'UNREAD',
  MENTIONS = 'MENTIONS',
  GROUPS = 'GROUPS',
  PERSONAL = 'PERSONAL',
  WITH_MY_LAST_ANSWER = 'WITH_MY_LAST_ANSWER',
  WITHOUT_MY_LAST_ANSWER = 'WITHOUT_MY_LAST_ANSWER',
  WHERE_I_AM_MODERATOR = 'WHERE_I_AM_MODERATOR',
}

type UseRoomFiltersOptions = {
  rooms?: ComputedRef<RoomListItem[]> | Ref<RoomListItem[]>
}

export const useRoomFilters = (options?: UseRoomFiltersOptions) => {

  const { getUnreadCount, isCurrentUserMentionedOrQuotedAtUnreadMessages } = chatModel.useChatMessageStore()
  const { roomsList } = storeToRefs(useStoreRooms())
  const sourceRooms = computed(() => options?.rooms?.value ?? roomsList.value)
  const { currentUser } = useCurrentUser()
  const { t } = useLocalization()

  const filters = ref([
    { label: t('common.roomItemFilterAll'), value: RoomItemFilter.ALL },
    { label: t('common.roomItemFilterUnread'), value: RoomItemFilter.UNREAD },
    { label: t('common.roomItemFilterMentions'), value: RoomItemFilter.MENTIONS },
    { label: t('common.roomItemFilterGroups'), value: RoomItemFilter.GROUPS },
    { label: t('common.roomItemFilterPersonal'), value: RoomItemFilter.PERSONAL },
    { label: t('common.roomItemFilterWithoutMy'), value: RoomItemFilter.WITHOUT_MY_LAST_ANSWER },
    { label: t('common.roomItemFilterWithMy'), value: RoomItemFilter.WITH_MY_LAST_ANSWER },
    { label: t('common.whereIAmModerator'), value: RoomItemFilter.WHERE_I_AM_MODERATOR },
  ])

  const selectedFilter = ref(RoomItemFilter.ALL)

  const isFilterActive = computed(() => selectedFilter.value !== RoomItemFilter.ALL)

  const selectedFilterLabel = computed(() => (
    filters.value.find((filter) => filter.value === selectedFilter.value)?.label ?? ''
  ))

  const filtered = computed(() => sourceRooms.value.filter((el) => {
    if (selectedFilter.value === RoomItemFilter.UNREAD) {
      return getUnreadCount(el.pServedUser) > 0
    }
    if (selectedFilter.value === RoomItemFilter.MENTIONS) {
      return isCurrentUserMentionedOrQuotedAtUnreadMessages(el.pServedUser)
    }
    if (selectedFilter.value === RoomItemFilter.GROUPS) {
      return !!el.isGroup
    }
    if (selectedFilter.value === RoomItemFilter.PERSONAL) {
      return !el.isGroup
    }
    const isMyLastAnswer = el.lastMessage?.item && 'executor' in el.lastMessage.item && el.lastMessage.item.executor === currentUser.value?.imLogin
    if (selectedFilter.value === RoomItemFilter.WITHOUT_MY_LAST_ANSWER) {
      return !isMyLastAnswer
    }
    if (selectedFilter.value === RoomItemFilter.WITH_MY_LAST_ANSWER) {
      return isMyLastAnswer
    }
    if (selectedFilter.value === RoomItemFilter.WHERE_I_AM_MODERATOR) {
      const moderators = el.contacts?.filter((c) => c.role === 'Moderator')
      return !!moderators?.find((m) => m.id === currentUser.value?.imLogin)
    }
    return true
  }))

  const clearFilter = () => {
    selectedFilter.value = RoomItemFilter.ALL
  }

  return {
    filtered,
    filters,
    selectedFilter,
    selectedFilterLabel,
    isFilterActive,
    clearFilter,
  }
}
